'use client';

import { Building2, ClipboardCheck, CreditCard, User, Users } from 'lucide-react';
import * as React from 'react';
import { useAuth } from '../services/auth-service';
import { Button } from './button';
import { InfoRow, InfoSection } from './info-section';
import { SubscriptionStatusBadge } from './subscription-status-badge';

type SubscriptionInfo = {
  status: 'active' | 'inactive' | 'pending' | 'processing' | 'unknown';
  hasEntitlement: boolean;
  hasBaa: boolean;
  seatCount?: number;
  stripeCustomerId?: string;
  organizationName?: string;
  lastChecked: string;
};

type SubscriptionDetailsCardProps = {
  subscription: SubscriptionInfo;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

/**
 * SubscriptionDetailsCard component
 *
 * Displays detailed subscription information including:
 * - Current status badge
 * - Organization details
 * - Seat count
 * - Last sync time
 * - Manual refresh button
 */
export const SubscriptionDetailsCard: React.FC<SubscriptionDetailsCardProps> = ({
  subscription,
  onRefresh,
  isRefreshing = false,
}) => {
  const { user } = useAuth();

  const formatTimestamp = (isoString: string) => {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);

      if (diffSecs < 60) {
        return `${diffSecs} second${diffSecs !== 1 ? 's' : ''} ago`;
      } else if (diffMins < 60) {
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleString();
      }
    } catch {
      return 'Unknown';
    }
  };

  return (
    <InfoSection title="Subscription Status" titleIcon={<CreditCard className="w-5 h-5" />}>
      <div className="flex justify-end -mt-8 flex-wrap mb-2">
        <SubscriptionStatusBadge status={subscription.status} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border/30">
        {/* Organization Name */}
        {subscription.organizationName && (
          <div className="space-y-1">
            <p className="text-secondary-enhanced text-sm flex items-center gap-1.5">
              <Building2 className="w-4 h-4" />
              <span>Organization</span>
            </p>
            <p className="text-base font-semibold pl-5.5">{subscription.organizationName}</p>
          </div>
        )}

        {/* User Role */}
        {user?.role && (
          <div className="space-y-1">
            <p className="text-secondary-enhanced text-sm flex items-center gap-1.5">
              <User className="w-4 h-4" />
              <span>Your Role</span>
            </p>
            <p className="text-base font-semibold pl-5.5 capitalize flex items-center gap-2">
              {user.role}
              {user.role === 'admin' && (
                <span className="glass-pill text-primary">Full Access</span>
              )}
            </p>
          </div>
        )}

        {/* Entitlement Status */}
        <div className="space-y-1">
          <p className="text-secondary-enhanced text-sm flex items-center gap-1.5">
            <CreditCard className="w-4 h-4" />
            <span>Subscription</span>
          </p>
          <p className="text-base font-semibold flex items-center gap-2 pl-5.5">
            {subscription.hasEntitlement ? (
              <>
                <span className="text-green-600 dark:text-green-400">✓</span>
                <span>Active</span>
              </>
            ) : (
              <>
                <span className="text-muted-foreground">○</span>
                <span>Not active</span>
              </>
            )}
          </p>
        </div>

        {/* BAA Status */}
        <div className="space-y-1">
          <p className="text-secondary-enhanced text-sm flex items-center gap-1.5">
            <ClipboardCheck className="w-4 h-4" />
            <span>BAA Agreement</span>
          </p>
          <p className="text-base font-semibold flex items-center gap-2 pl-5.5">
            {subscription.hasBaa ? (
              <>
                <span className="text-green-600 dark:text-green-400">✓</span>
                <span>Signed</span>
              </>
            ) : (
              <>
                <span className="text-muted-foreground">○</span>
                <span>Not signed</span>
              </>
            )}
          </p>
        </div>

        {/* Seat Count */}
        {subscription.seatCount !== undefined && (
          <div className="space-y-1">
            <p className="text-secondary-enhanced text-sm flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span>Team Seats</span>
            </p>
            <p className="text-base font-semibold pl-5.5">
              {subscription.seatCount} {subscription.seatCount === 1 ? 'seat' : 'seats'}
            </p>
          </div>
        )}
      </div>

      {/* Last Updated & Refresh */}
      <div className="pt-2 border-t border-border/30 flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-secondary-enhanced text-xs">Last updated</p>
          <p className="text-sm font-semibold">{formatTimestamp(subscription.lastChecked)}</p>
        </div>

        {onRefresh && (
          <Button
            onClick={onRefresh}
            disabled={isRefreshing}
            variant="secondary"
            size="sm"
            title="Refresh subscription status"
          >
            <span className={isRefreshing ? 'animate-spin' : ''}>⟳</span>
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </Button>
        )}
      </div>

      {/* Status-specific Messages */}
      {subscription.status === 'processing' && (
        <div className="glass-surface p-3 rounded-lg border border-primary/20">
          <p className="text-secondary-enhanced text-sm">
            <strong className="text-primary">⏳ Processing:</strong> Your payment was successful!
            We're syncing your subscription with WorkOS. This usually takes less than a minute, but
            can occasionally take 2-5 minutes. The page will automatically update when ready.
          </p>
        </div>
      )}

      {subscription.status === 'pending' && (
        <div className="glass-surface p-3 rounded-lg border border-yellow-500/20">
          <p className="text-secondary-enhanced text-sm">
            <strong className="text-yellow-600 dark:text-yellow-400">⏳ Almost there:</strong> Your
            subscription is active, but you need to sign the BAA agreement to access chat features.
          </p>
        </div>
      )}
    </InfoSection>
  );
};

SubscriptionDetailsCard.displayName = 'SubscriptionDetailsCard';
